import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded'
import WhatsAppIcon from '@mui/icons-material/WhatsApp'
import { useTranslation } from 'next-i18next'
import Image from 'next/image'
import Link from 'next/link'
import React from 'react'
import useWhatsappCityHint from 'src/hooks/useWhatsappCityHint'
import ga from 'src/utils/pixels/gtag'
import { appendCityHint, buildWhatsappUrl } from 'src/utils/whatsapp'

type VerticalItem = {
  title: string
  /** One line. The tile has no room for a paragraph and the pillar page
   *  carries the full pitch. */
  blurb: string
  cta: string
  href: string
  secondary_cta?: string
  secondary_href?: string
  /** When set, the secondary CTA opens WhatsApp with this prefilled message
   *  instead of an internal link. Used for B2B verticals (Bodegas Comerciales,
   *  Naves Industriales, Flotillas) where "habla con un especialista" hands
   *  off to the sales chat. */
  secondary_whatsapp_msg?: string
  price: string
  picture: string
  alt: string
}

/** Source assets whose JPGs have baked-in rounded corners + white edge masks
 *  (originally cropped for PersonalizedLanding's rounded chrome). Rendering
 *  them as-is in our sharp hairline container exposes the white corners. A
 *  CSS scale-110 on the Image pushes the masked zone outside the parent's
 *  overflow-hidden clip area. List grows when more rounded-mask sources are
 *  reused on editorial surfaces. */
const ROUNDED_MASK_SOURCES = new Set<string>([
  '/images/personalized-minibodegas.jpg',
  '/images/personalized-bodegas.jpg'
])

const cropZoomRounded = (picture: string): boolean =>
  ROUNDED_MASK_SOURCES.has(picture)

/** "Ver estacionamientos" → ["Ver ", "estacionamientos"]. The tail is rendered
 *  in a nowrap span together with the arrow glyph, so a CTA that has to wrap
 *  in a narrow tile never strands the arrow on a line of its own. */
const splitLastWord = (label: string): [string, string] => {
  const cut = label.lastIndexOf(' ')
  return cut === -1
    ? ['', label]
    : [label.slice(0, cut + 1), label.slice(cut + 1)]
}

/** The storage tile is the strategic one — it carries the paid spend and a
 *  ~2.6× better LTV than parking (docs/marketing/pillar-economics.md) — so it
 *  gets double width on desktop and a full-width row on mobile. Hierarchy
 *  through scale, not through a fifth identical card. */
const FEATURED_INDEX = 0

type TileProps = {
  item: VerticalItem
  index: number
  featured: boolean
}

const VerticalTile = ({ item, index, featured }: TileProps) => {
  const cityHintLine = useWhatsappCityHint()
  const whatsappHref = item.secondary_whatsapp_msg
    ? buildWhatsappUrl(
        appendCityHint(item.secondary_whatsapp_msg, cityHintLine)
      )
    : null
  const secondaryHref = whatsappHref ?? item.secondary_href
  const [ctaHead, ctaTail] = splitLastWord(item.cta)

  // `vertical` = the row's pillar path (stable identity even when the click
  // itself opens WhatsApp). NOT `contact_wa` — that name is reserved for
  // listing-level intent.
  const track = (cta: 'primary' | 'secondary' | 'whatsapp_specialist') =>
    ga.event('home_section_cta_click', {
      section: 'verticals',
      cta,
      vertical: item.href
    })

  return (
    // `@container` — the title and CTA size themselves off the TILE width
    // (cqw / @min-[]), not the viewport. Six tiles in a 1280px container
    // leave each non-featured tile ~198px, and viewport breakpoints can't
    // see that; before this, every non-featured title wrapped to two lines
    // at 1440 and "Estacionamientos" got hyphenated (Jules, 2026-09-06).
    <article
      // The stretched primary link suppresses its own outline; the tile
      // carries the focus ring instead (`has-[…]`), so keyboard focus reads
      // as "this whole tile", matching what a tap opens.
      className={`group @container relative flex overflow-hidden border border-ink-100/15 bg-white-500 transition-colors duration-200 focus-within:border-blue-500 hover:border-ink-100/40 has-[a:first-of-type:focus-visible]:outline-2 has-[a:first-of-type:focus-visible]:outline-offset-2 has-[a:first-of-type:focus-visible]:outline-ink-700 ${
        featured
          ? 'col-span-2 flex-row md:col-span-2 md:flex-col'
          : 'col-span-1 flex-col'
      }`}
    >
      {/* Image — a window, not card chrome: hairline border, no radius, no
          shadow. The blue multiply tint ties five unrelated stock photos to
          the brand; it is lighter than the old full-row treatment so the
          spaces still read as different spaces at tile size. */}
      <div
        className={`relative shrink-0 overflow-hidden bg-cream-300 ${
          featured ? 'w-[42%] md:aspect-[4/3] md:w-full' : 'aspect-[4/3] w-full'
        }`}
      >
        <Image
          src={item.picture}
          alt={item.alt}
          fill
          className={`object-cover ${cropZoomRounded(item.picture) ? 'scale-110' : ''}`}
          sizes={
            featured
              ? '(min-width: 900px) 34vw, 42vw'
              : '(min-width: 900px) 17vw, 50vw'
          }
          loading={index === FEATURED_INDEX ? 'eager' : 'lazy'}
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 bg-gradient-to-br from-blue-700/45 via-blue-500/25 to-transparent mix-blend-multiply"
        />
      </div>

      <div
        className={`flex min-w-0 flex-1 flex-col gap-1.5 ${featured ? 'p-4 md:p-5' : 'p-3 md:p-4'}`}
      >
        <p className="text-xs font-medium text-blue-500">{item.price}</p>
        {/* Non-featured title is fluid on desktop: 9.5cqw puts the longest
            single-word title ("Estacionamientos", ~8.7em) on ONE line at the
            198px tile a 1280px container yields, and caps at 20px on wider
            tiles. Below ~170px (a 1024 viewport) it floors at 16px and the
            word hyphenates rather than overflowing — that is the last resort
            `hyphens-auto` is kept for. NO `text-balance` here: combined with
            `hyphens-auto` it "balanced" "Bodegas Comerciales" into
            "Bode- / gas Comerciales" at 1440 (seen 2026-09-06). */}
        <h3
          className={`font-display leading-tight font-medium break-words hyphens-auto text-ink-700 ${
            featured
              ? 'text-[24px] md:text-[28px]'
              : 'text-[17px] md:text-[clamp(16px,9.5cqw,20px)] md:tracking-[-0.01em]'
          }`}
        >
          {item.title}
        </h3>
        <p className="text-[13px] leading-snug text-ink-200 md:text-sm">
          {item.blurb}
        </p>

        <div className="mt-auto flex flex-wrap items-center gap-x-4 gap-y-1.5 pt-3">
          {/* Stretched link: the ::after covers the whole tile so the
              tile is one tap target, while the accessible name stays the
              CTA text. The secondary link sits above it (z-10). */}
          {/* Links flow as INLINE text, not inline-flex: when a label has to
              wrap in a narrow tile the line breaks between words, and the
              arrow / WhatsApp glyph stays glued to the word it belongs to
              (last word + glyph share a nowrap span) instead of floating
              mid-height beside a two-line label or dropping onto a line of
              its own. Non-featured tiles run the CTA at 12px, stepping up to
              13px on a single line once the tile is ≥190px wide — every
              label is ≤20 chars, which fits there. */}
          <Link
            href={item.href}
            onClick={() => track('primary')}
            className={`inline font-semibold text-blue-500 after:absolute after:inset-0 after:content-[''] focus-visible:outline-none ${
              featured
                ? 'text-sm'
                : 'text-xs @min-[190px]:text-[13px] @min-[190px]:whitespace-nowrap'
            }`}
          >
            {ctaHead}
            <span className="whitespace-nowrap">
              {ctaTail}
              <ArrowForwardRoundedIcon className="ml-1 inline-block align-[-3px] text-[16px] transition-transform duration-200 group-hover:translate-x-0.5" />
            </span>
          </Link>
          {item.secondary_cta && secondaryHref && (
            <Link
              href={secondaryHref}
              onClick={() =>
                track(whatsappHref ? 'whatsapp_specialist' : 'secondary')
              }
              {...(whatsappHref
                ? { target: '_blank', rel: 'noopener noreferrer' }
                : {})}
              // Vertical padding on an INLINE box grows the hit area (to
              // ~40px) without moving the line box — the 12px label alone
              // was a ~16px target sitting over the stretched primary link.
              className="relative z-10 inline py-3 text-xs font-medium text-ink-700 underline decoration-ink-100/40 underline-offset-4 transition-colors hover:text-blue-500 hover:decoration-blue-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink-700"
            >
              {whatsappHref && (
                <WhatsAppIcon className="mr-1 inline-block align-[-2px] text-[14px]" />
              )}
              <span>{item.secondary_cta}</span>
            </Link>
          )}
        </div>
      </div>
    </article>
  )
}

/**
 * One row of five tiles — every space type SpotMe brokers, in a single
 * viewport. Replaced the five alternating image/text rows (4,000 px on a
 * phone, 38% of the page, 8 clicks in seven weeks) on 2026-09-06. The hero
 * already offers the same destinations as CTAs and chips; this row exists
 * for the visitor who scrolled past them wanting to see the whole menu with
 * a price anchor per line, not to re-pitch each one.
 */
const VerticalsSection = () => {
  const { t } = useTranslation('landing')
  const items = t('INDEX_VERTICALS.ITEMS', {
    returnObjects: true
  }) as unknown as VerticalItem[]

  return (
    <section className="w-full bg-cream-300 px-5 py-14 md:px-10 md:py-20">
      <div className="mx-auto w-full max-w-7xl">
        <h2 className="max-w-2xl font-display text-[28px] leading-[1.15] font-medium text-ink-700 md:text-[40px]">
          {t('INDEX_VERTICALS.HEADING')}
        </h2>

        {/* 2-col on mobile with the featured tile spanning the first row;
            6-col on desktop with the featured tile taking two. */}
        <div className="mt-8 grid grid-cols-2 gap-3 md:mt-10 md:grid-cols-6 md:gap-4">
          {items.map((item, index) => (
            <VerticalTile
              key={item.href}
              item={item}
              index={index}
              featured={index === FEATURED_INDEX}
            />
          ))}
        </div>
      </div>
    </section>
  )
}

export default React.memo(VerticalsSection)
